import { Injectable, Renderer2, RendererFactory2, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Exception, Pagination, PaymentHistoryResponse } from '@cargos/sprintpay-models';
import { PaymentsService } from '@cargos/sprintpay-services';
import { Observable, map, of, switchMap, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class PaymentsFacilityService {
    private renderer: Renderer2;

    constructor(
        private rendererFactory: RendererFactory2,
        private paymentsService: PaymentsService,
        private domSanitizer: DomSanitizer
    ) {
        this.renderer = this.rendererFactory.createRenderer(null, null);
    }

    getCompletedPayments({
        page,
        pageSize,
        startDate,
        endDate,
        sort,
        date,
    }: {
        page: number;
        pageSize: number;
        startDate?: string;
        endDate?: string;
        sort?: string;
        date?: string | number;
    }): Observable<Pagination<PaymentHistoryResponse>> {
        return this.paymentsService.getCompletedPayments({
            page,
            pageSize,
            startDate,
            endDate,
            sort,
            date,
        });
    }

    searchTerms({
        searchTerms,
        page,
        pageSize,
        startDate,
        endDate,
        sort,
        date,
    }: {
        searchTerms: string;
        page: number;
        pageSize: number;
        startDate?: string;
        endDate?: string;
        sort?: string;
        date?: string | number;
    }): Observable<Pagination<PaymentHistoryResponse>> {
        return this.paymentsService.searchTerms({
            searchTerms,
            page,
            pageSize,
            startDate,
            endDate,
            sort,
            date,
        });
    }

    getPaymentConfirmation(
        recordID: number,
        config: { open?: boolean; download?: boolean } = { open: false, download: true }
    ): Observable<void> {
        return this.paymentsService.getPaymentConfirmation(recordID).pipe(
            map((fileResponse) => {
                const link: HTMLAnchorElement = this.renderer.createElement('a');
                const file: Blob = new Blob([fileResponse], { type: fileResponse.type });
                const url = this.domSanitizer.sanitize(
                    SecurityContext.URL,
                    this.domSanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(file))
                );

                if (url) {
                    link.href = url;

                    if (config.open) {
                        window.open(url, '_blank');
                    }

                    if (config.download) {
                        link.download = `payment-confirmation-${recordID}.pdf`;
                        link.click();
                    }
                    window.URL.revokeObjectURL(url);
                }
            })
        );
    }

    getPaymentConfirmations(
        paymentsIds: string[],
        downloadFormat = 'PDF',
        startDate?: string,
        endDate?: string
    ): Observable<string> {
        return this.paymentsService.getPaymentConfirmations(paymentsIds, downloadFormat, startDate, endDate).pipe(
            switchMap((fileResponse) => {
                if (!fileResponse) {
                    return throwError(() =>
                        Exception.fromJson({
                            description: 'Looks like there is no information.',
                            type: 'warning',
                        })
                    );
                }

                const link: HTMLAnchorElement = this.renderer.createElement('a');
                const file: Blob = new Blob([fileResponse], {
                    type: fileResponse.type,
                });
                const url = this.domSanitizer.sanitize(
                    SecurityContext.URL,
                    this.domSanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(file))
                );

                if (url) {
                    this.renderer.setAttribute(link, 'href', url);
                    this.renderer.setAttribute(link, 'download', this.getFileName(paymentsIds, downloadFormat));
                    link.click();
                    window.URL.revokeObjectURL(url);
                    return of('Done');
                }

                return throwError(() => Exception.fromJson({ description: 'Error downloading file' }));
            })
        );
    }

    getFileName(paymentsIds: string[], extension: string): string {
        const extensionName = extension.toLowerCase();

        if (paymentsIds?.length) {
            if (paymentsIds?.length > 1 && extension === 'PDF') {
                return 'Payments_Confirmation.zip';
            } else if (paymentsIds?.length > 1) {
                return `Payments_Confirmation.${extensionName}`;
            }

            return `Payment_Confirmation_${paymentsIds?.[0]}.${extensionName}`;
        }

        return `Payment_Confirmation.${extensionName}`;
    }

    getReceiptByPost(
        invoices: number[],
        confirmationId: string,
        config: { open?: boolean; download?: boolean } = { open: false, download: true }
    ): Observable<string> {
        const formData = {
            invoices,
            confirmationId,
        };
        return this.paymentsService.getReceiptByPost(formData).pipe(
            switchMap((fileResponse) => {
                if (!fileResponse) {
                    return throwError(() =>
                        Exception.fromJson({
                            description: 'Looks like there is no information.',
                            type: 'warning',
                        })
                    );
                }
                const link: HTMLAnchorElement = this.renderer.createElement('a');
                const fileName = `Receipt-${confirmationId}`;
                const file = new Blob([fileResponse], { type: fileResponse.type });

                const url = this.domSanitizer.sanitize(
                    SecurityContext.URL,
                    this.domSanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(file))
                );

                if (url) {
                    link.href = url;

                    if (config.open) {
                        window.open(url, '_blank');
                    }

                    if (config.download) {
                        link.download = `payment-confirmation-${fileName}.pdf`;
                        link.click();
                    }
                    window.URL.revokeObjectURL(url);
                    return of('Done');
                }

                return throwError(() => Exception.fromJson({ description: 'Error downloading file' }));
            })
        );
    }
}
